<script setup>
  import {computed, ref} from "vue";

  import ComponentInput from "@components/Inputs/Input.vue";
  import {useForm} from "@inertiajs/vue3";
  import {cn} from "@pharma4u/medicheck-components/utils";

  defineOptions({
    name: "PagesRecordResultSignature",
  });

  const props = defineProps({
    record: {
      type: Object,
      required: true,
    },
  });

  const today = new Date().toISOString().split("T")[0];
  const analyzedAt = ref(props.record.analyzed_at); // Format: Y-m-d
  const formattedAnalyzedAt = computed(() => {
    return new Date(analyzedAt.value).toLocaleDateString("de-DE", {dateStyle: "medium"});
  });

  const form = useForm({
    analyzed_by: props.record.analyzed_by,
  });

  const abdataVersion = computed(() => {
    return props.record?.metadata?.version?.abdata;
  });

  function openDatePicker(event) {
    if (document.activeElement !== event.target) {
      // input is not focused, showPicker() will not work
      return;
    }

    event.target.blur();
    event.target.showPicker();
  }

  function save() {
    form.patch(route("records.update", {patient: props.record.patient_id, record: props.record.id}), {
      preserveScroll: true,
      only: ["flash", "errors", "record"],
    });
  }
</script>

<template>
  <section class="print:break-inside-avoid-page print:pt-20" :data-test="getTestId('signature')">
    <div class="flex space-x-3 print:hidden">
      <div>
        <input
          v-model="analyzedAt"
          :min="record.analyzed_at"
          :max="today"
          type="date"
          aria-label="Datum"
          :class="cn('flex-1', {'cursor-pointer': record.analyzed_at !== today})"
          :test-id="getTestId('date')"
          :disabled="record.analyzed_at === today"
          @focus.prevent="openDatePicker"
          @click.prevent="openDatePicker"
          @keydown.prevent
          @paste.prevent
        />
        <div class="mt-1 text-xs text-gray-500" :data-test="getTestId('input-helper-text')">Datum</div>
      </div>

      <ComponentInput
        v-model="form.analyzed_by"
        helper-text="Name, Unterschrift des verantwortlichen Apothekers"
        :hide-clear="true"
        :test-id="getTestId('signature')"
        @blur="save()"
        @keydown.enter="$event.target.blur()"
      />
    </div>

    <div class="hidden space-x-3 print:flex">
      <div>
        <div class="h-7 border-b-1 border-gray-300 text-sm">
          {{ formattedAnalyzedAt }}
        </div>
        <div class="mt-1 text-xs text-gray-500">Datum</div>
      </div>

      <div class="flex-1">
        <div class="h-7 border-b-1 border-gray-300 text-sm">
          {{ form.analyzed_by }}
        </div>
        <div class="mt-1 text-xs text-gray-500">Name, Unterschrift des verantwortlichen Apothekers</div>
      </div>
    </div>

    <div v-if="abdataVersion" class="cursor-default py-8 text-sm text-gray-400">
      ABDATA Datenbestand vom {{ abdataVersion }}
    </div>
  </section>
</template>
